
import store from "../store/store";
import { ICarType, IUserType } from "../types";
import { firebase } from "../config/firebase.client";
import { onValue, update, set, push, get } from "firebase/database";
import { FETCH_CARS, FETCH_CARS_SUCCESS, FETCH_CARS_FAILED } from "../store/types";
import { uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";

export const fetchCars = () => (dispatch: any) => {
  const { carsRef } = firebase;

  try {
    const userInfo = store.getState().auth.profile;

    if (!userInfo || !userInfo.uid) {
      console.error("Usuário não autenticado ou sem UID");
      dispatch({
        type: FETCH_CARS_FAILED,
        payload: "Usuário não autenticado",
      });
      return;
    }


    const carReference = carsRef(userInfo.uid, userInfo.usertype);

    if (!carReference) {
      console.error("carsRef retornou null/undefined");
      dispatch({
        type: FETCH_CARS_FAILED,
        payload: "Referência do carro inválida",
      });
      return;
    }

    dispatch({
      type: FETCH_CARS,
      payload: null,
    });

    onValue(carReference, (snapshot) => {
      if (snapshot.val()) {
        const dataObj: Record<string, ICarType> = snapshot.val();
        const allCars: ICarType[] = Object.entries(dataObj).map(([id, car]) => ({
          ...car,
          id,
        }));

        const filteredCars = userInfo.usertype === 'fleetadmin'
          ? allCars.filter(car =>
            car.fleetadmin?.includes(userInfo.uid)
          )
          : allCars;

        dispatch({
          type: FETCH_CARS_SUCCESS,
          payload: filteredCars,
        });
      } else {
        dispatch({
          type: FETCH_CARS_FAILED,
          payload: store.getState().languagedata.defaultLanguage.no_cars,
        });
      }
    }, (error) => {
      console.error("Erro no onValue:", error);
      dispatch({
        type: FETCH_CARS_FAILED,
        payload: error.message || "Erro ao buscar carros",
      });
    });

  } catch (e: any) {
    console.error("Erro no fetchCars:", e);
    dispatch({
      type: FETCH_CARS_FAILED,
      payload: e.message || "Erro ao buscar carros",
    });
  }
};

export const carform = async (data: Partial<ICarType>, type: 'edit' | 'delete' | 'create', fleetadmin?: IUserType['fleetadmin']) => {
  const { auth, carAddRef, singleUserRef, carEditRef, carImage, settingsRef } = firebase;

  if (!auth?.currentUser?.uid) {
    console.error("Usuário não autenticado");
    return null;
  }


  const settingsdata = await get(settingsRef);
  const settings = settingsdata.val();


  try {
    const userId = auth.currentUser.uid;
    let carId;

    const deleteImages = async (carId: ICarType['id']) => {
      const imageIndices = [0, 1, 2, 3, 4];

      for (const index of imageIndices) {
        try {
          const imageRef = carImage(carId, index);
          await getDownloadURL(imageRef).catch(() => null);
          await deleteObject(imageRef);
        } catch (error) {
          console.error(`Imagem ${index} não encontrada ou já deletada`);
        }
      }
    };

    const processImages = async (carId: ICarType['id']) => {
      const imageFields = {
        car_image_frente: data.car_image_frente,
        car_image_traseira: data.car_image_traseira,
        car_image_documento: data.car_image_documento,
        car_image_lateraldireita: data.car_image_lateraldireita,
        car_image_lateralesquerda: data.car_image_lateralesquerda,
      };

      const imageIndexMap: any = {
        car_image_frente: 0,
        car_image_traseira: 3,
        car_image_documento: 4,
        car_image_lateraldireita: 1,
        car_image_lateralesquerda: 2,
      };

      const processedImages: any = {};

      const uploadAndGetURL = async (blobData: any, index: any) => {
        try {
          const storageRef = carImage(carId, index);

          const uploadTask = uploadBytesResumable(storageRef, blobData);

          return await new Promise((resolve, reject) => {
            uploadTask.on(
              'state_changed',
              () => { },
              (error) => {
                console.error(error)


                if (error.customData && error.customData.serverResponse) {
                  console.error('Server Response RAW:', error.customData.serverResponse);
                  try {
                    const parsed = JSON.parse(error.customData.serverResponse);
                    console.error('Server Response JSON:', parsed);
                  } catch (e) {
                  }
                }

                reject(error);
              },
              async () => {
                try {
                  const url = await getDownloadURL(uploadTask.snapshot.ref);
                  resolve(url);
                } catch (error) {
                  reject(error);
                }
              }
            );
          });
        } catch (error) {
          console.error('Erro no try-catch externo:', error);
          throw error;
        }
      };

      for (const [fieldName, imageData] of Object.entries(imageFields)) {
        if (imageData && typeof imageData === 'object' && imageData !== null) {
          try {
            const index = imageIndexMap[fieldName];
            const url = await uploadAndGetURL(imageData, index);
            processedImages[fieldName] = url;
          } catch (error) {
            console.error(`Erro ao fazer upload da imagem ${fieldName}:`, error);
            processedImages[fieldName] = '';
          }
        } else if (typeof imageData === 'string') {
          processedImages[fieldName] = imageData;
        } else {
          processedImages[fieldName] = '';
        }
      }

      return processedImages;
    };


    if (type === 'delete') {
      if (!data.id) {
        console.error("Não foi possível deletar o carro, pois ele não tem id.");
        return null;
      }

      carId = data.id;

      await deleteImages(carId);

      if (carEditRef) {
        await set(carEditRef(carId), null);
      }

      const userUpdateData = {
        carType: null,
        car_image: null,
        carApproved: null,
        vehicleMake: null,
        vehicleModel: null,
        vehicleNumber: null,
        updateAt: new Date().getTime(),
      };

      await update(singleUserRef(userId), userUpdateData);

      return carId;
    }

    const baseCarData: Partial<ICarType> = {
      carType: data.carType || [],
      active: data.active || false,
      driver: data.driver || userId,
      updateAt: new Date().getTime(),
      approved: data.approved || false,
      other_info: data.other_info || "",
      vehicleMake: data.vehicleMake || "",
      vehicleYear: data.vehicleYear || "",
      driverImage: data.driverImage || null,
      vehicleModel: data.vehicleModel || "",
      driverMobile: data.driverMobile || null,
      vehicleNumber: data.vehicleNumber || "",
      driverLastName: data.driverLastName || null,
      driverFirstName: data.driverFirstName || null,
    };

    let processedImages: any = {};

    if (type === 'edit') {
      if (!data.id) {
        console.error("Não foi possível editar o carro, pois ele não tem id.");
        return null;
      }

      carId = data.id;
      processedImages = await processImages(carId);

      const finalCarData: ICarType = {
        ...baseCarData,
        ...processedImages,
        ...(fleetadmin && { fleetadmin }),
      };


      await update(carEditRef(carId), finalCarData);

    } else {
      const newCarRef = push(carAddRef);
      if (!newCarRef.key) {
        console.error("Não foi possível gerar ID para o carro");
        return null;
      }

      carId = newCarRef.key;
      processedImages = await processImages(carId);


      const finalCarData: ICarType = {
        ...baseCarData,
        userId,
        id: carId,
        createdAt: new Date().getTime(),
        ...(fleetadmin && { fleetadmin }),
        approved: settings?.carApproval ? false : true,
        ...processedImages,
      };

      await set(newCarRef, finalCarData);
    }

    if (baseCarData.active) {
      const updateDataNew: Partial<IUserType> = {
        updateAt: new Date().getTime(),
      };

      if (processedImages.car_image_frente) {
        updateDataNew.car_image = processedImages.car_image_frente;
      }

      if (baseCarData.carType?.length) updateDataNew.carType = baseCarData.carType;
      if (baseCarData.approved !== undefined) updateDataNew.carApproved = baseCarData.approved;
      if (baseCarData.other_info) updateDataNew.other_info = baseCarData.other_info;
      if (baseCarData.vehicleMake) updateDataNew.vehicleMake = baseCarData.vehicleMake;
      if (baseCarData.vehicleNumber) updateDataNew.vehicleNumber = baseCarData.vehicleNumber;
      if (baseCarData.vehicleModel) updateDataNew.vehicleModel = baseCarData.vehicleModel;
      if (baseCarData.vehicleYear) updateDataNew.vehicleYear = baseCarData.vehicleYear;


      await update(singleUserRef(userId), updateDataNew);
    }

    return carId;

  } catch (error) {
    console.error("Erro no carform:", error);
    return null;
  }
};
